import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { quizApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './Quiz.css';

function Quiz() {
  const navigate = useNavigate();
  const { user, isAuthenticated, refreshUser } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [firstUnansweredId, setFirstUnansweredId] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [gradedQuestions, setGradedQuestions] = useState([]);
  const [profileUpgraded, setProfileUpgraded] = useState(false);

  const loadQuestions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await quizApi.getQuestions();
      if (response.data.success) {
        setQuestions(response.data.data || response.data);
        setAnswers({});
        setScore(null);
        setShowResults(false);
        setGradedQuestions([]);
        setFirstUnansweredId(null);
      } else {
        setError(response.data.message || 'Failed to load questions');
      }
    } catch (err) {
      console.error('Error loading questions:', err);
      setError('Failed to load quiz questions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  // Scroll to top when results are shown
  useEffect(() => {
    if (showResults) {
      // Small delay to ensure DOM has updated
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  }, [showResults]);

  const handleAnswerChange = (questionId, answer) => {
    setAnswers({
      ...answers,
      [questionId]: answer,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that all questions are answered
    const unansweredQuestions = questions.filter(
      (q) => !answers[q.question_id]
    );
    
    if (unansweredQuestions.length > 0) {
      setFirstUnansweredId(unansweredQuestions[0].question_id);
      setError(`Please answer all questions. You have ${unansweredQuestions.length} unanswered.`);
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      // Grade the quiz
      let correctCount = 0;
      const graded = questions.map((question) => {
        const userAnswer = answers[question.question_id];
        const isCorrect = userAnswer === question.correct_ans;
        if (isCorrect) correctCount++;
        
        return {
          ...question,
          userAnswer,
          isCorrect,
          answerClass: isCorrect ? 'correct' : 'incorrect',
        };
      });

      const finalScore = correctCount;
      setScore(finalScore);
      setGradedQuestions(graded);
      setShowResults(true);

      // Submit results to backend
      try {
        const response = await quizApi.submitResults({
          score: finalScore,
          answers: answers,
        });
        
        // Check if profile was upgraded
        if (response.data?.data?.profile_upgraded) {
          setProfileUpgraded(true);
          // Refresh user data to get updated profile
          // This will update the AuthContext and show club menu options
          if (isAuthenticated) {
            await refreshUser();
          }
        }
        
        // If anonymous user passed, redirect to register
        if (passed && !isAuthenticated) {
          // Store quiz pass flag in localStorage
          localStorage.setItem('quiz_passed', 'true');
          // Redirect to register page
          setTimeout(() => {
            navigate('/register');
          }, 2000); // Give user time to see the success message
        }
      } catch (submitError) {
        console.error('Error submitting results:', submitError);
        // Don't show error to user - results are already displayed
      }
    } catch (err) {
      console.error('Error grading quiz:', err);
      setError('Failed to grade quiz. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetake = () => {
    loadQuestions();
  };

  const handleScrollToFirstUnanswered = () => {
    if (!firstUnansweredId) return;
    const el = document.getElementById(`question-${firstUnansweredId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.focus({ preventScroll: true });
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="container quiz-container">
          <div className="loading">Loading quiz questions...</div>
        </div>
        <Footer />
      </>
    );
  }

  const passed = score !== null && score >= 8;

  return (
    <>
      <Header />
      <div className="container quiz-container">
        <div className="quiz-content">
          {!showResults && (
            <div className="quiz-intro">
              <h1>GuyRules Quiz</h1>
              <p className="quiz-description">
                If you can pass this test, you are invited to join ClubGuy and submit your own GuyRules to be voted on.
              </p>
              <p className="quiz-instructions">
                Answer all 10 questions. You need 8 correct answers to pass.
              </p>
            </div>
          )}

          {error && (
            <div className="alert alert-danger" role="alert">
              <span>{error} </span>
              {firstUnansweredId && (
                <button
                  type="button"
                  className="btn btn-link p-0 align-baseline"
                  onClick={handleScrollToFirstUnanswered}
                >
                  Click to jump to the first unanswered question.
                </button>
              )}
            </div>
          )}

          {showResults && (
            <div className={`quiz-results ${passed ? 'passed' : 'failed'}`}>
              <h2>Your Final Score: {score} out of {questions.length}</h2>
              {passed ? (
                <>
                  {isAuthenticated ? (
                    <div className="alert alert-success">
                      <strong>You PASSED!</strong>
                      {profileUpgraded ? (
                        <div style={{ marginTop: '1rem' }}>
                          <p><strong>🎉 Congratulations! Your account has been upgraded to Club member!</strong></p>
                          <p>You now have access to the following benefits:</p>
                          <ul style={{ textAlign: 'left', marginTop: '0.5rem' }}>
                            <li>Submit new rules for voting</li>
                            <li>Vote on club submissions</li>
                            <li>Access club-only features</li>
                          </ul>
                        </div>
                      ) : (
                        <div style={{ marginTop: '1rem' }}>
                          <p><strong>🎉 Congratulations! Your account will be upgraded to Club member!</strong></p>
                          <p>As a Club member, you will have access to:</p>
                          <ul style={{ textAlign: 'left', marginTop: '0.5rem' }}>
                            <li>Submit new rules for voting</li>
                            <li>Vote on club submissions</li>
                            <li>Access club-only features</li>
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="alert alert-success">
                      <strong>You PASSED!</strong>
                      <p style={{ marginTop: '1rem' }}>
                        You're being redirected to create your ClubGuy account...
                      </p>
                      <p style={{ marginTop: '0.5rem' }}>
                        After successful registration, your account will automatically be set to <strong>Club member</strong> with access to:
                      </p>
                      <ul style={{ textAlign: 'left', marginTop: '0.5rem' }}>
                        <li>Submit new rules for voting</li>
                        <li>Vote on club submissions</li>
                        <li>Access club-only features</li>
                      </ul>
                      <p style={{ marginTop: '1rem' }}>
                        <Link to="/register" className="alert-link">
                          Click here if you're not redirected automatically
                        </Link>
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="alert alert-danger">
                  <strong>You FAILED!</strong>
                  <p>Go back and study more GuyRules. You need 8 of 10 correct answers.</p>
                  <button onClick={handleRetake} className="btn btn-primary">
                    Retake Quiz
                  </button>
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="questions-list">
              {questions.map((question, index) => (
                <div
                  key={question.question_id}
                  id={`question-${question.question_id}`}
                  className={`question-card ${
                    showResults
                      ? gradedQuestions.find((q) => q.question_id === question.question_id)
                          ?.answerClass || ''
                      : ''
                  }`}
                  tabIndex={-1}
                >
                  <div className="question-header">
                    <h3>Question #{index + 1}</h3>
                    {showResults && (
                      <span
                        className={`answer-indicator ${
                          gradedQuestions.find((q) => q.question_id === question.question_id)
                            ?.isCorrect
                            ? 'correct'
                            : 'incorrect'
                        }`}
                      >
                        {gradedQuestions.find((q) => q.question_id === question.question_id)
                          ?.isCorrect
                          ? '✓ Correct'
                          : '✗ Incorrect'}
                      </span>
                    )}
                  </div>
                  <p className="question-text">{question.question}</p>
                  
                  <div className="answer-options">
                    <label className="answer-option">
                      <input
                        type="radio"
                        name={`question_${question.question_id}`}
                        value="A"
                        checked={answers[question.question_id] === 'A'}
                        onChange={() => handleAnswerChange(question.question_id, 'A')}
                        disabled={showResults}
                      />
                      <span>A. {question.ans1}</span>
                    </label>
                    <label className="answer-option">
                      <input
                        type="radio"
                        name={`question_${question.question_id}`}
                        value="B"
                        checked={answers[question.question_id] === 'B'}
                        onChange={() => handleAnswerChange(question.question_id, 'B')}
                        disabled={showResults}
                      />
                      <span>B. {question.ans2}</span>
                    </label>
                    {question.ans3 && (
                      <label className="answer-option">
                        <input
                          type="radio"
                          name={`question_${question.question_id}`}
                          value="C"
                          checked={answers[question.question_id] === 'C'}
                          onChange={() => handleAnswerChange(question.question_id, 'C')}
                          disabled={showResults}
                        />
                        <span>C. {question.ans3}</span>
                      </label>
                    )}
                    {question.ans4 && (
                      <label className="answer-option">
                        <input
                          type="radio"
                          name={`question_${question.question_id}`}
                          value="D"
                          checked={answers[question.question_id] === 'D'}
                          onChange={() => handleAnswerChange(question.question_id, 'D')}
                          disabled={showResults}
                        />
                        <span>D. {question.ans4}</span>
                      </label>
                    )}
                  </div>

                  {showResults && (
                    <div className="correct-answer">
                      <strong>Correct Answer: {question.correct_ans}</strong>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {!showResults && (
              <div className="quiz-actions">
                <button
                  type="submit"
                  className="btn btn-success btn-lg"
                  disabled={submitting}
                >
                  {submitting ? 'Grading...' : 'Grade my Test'}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default Quiz;
